import React, { useState, useMemo, useEffect } from 'react';
import { categoryApi } from '../api/categoryApi';
import { Dropdown, Message } from 'semantic-ui-react';
import { PageContent, PageHeader, PageSection } from 'common/styles/page';
import { StyledAddButton } from 'common/components/button/StyledButton';

import { SurveystructureComplianceRead } from 'store/generated/admin/category';
import { useNavigate } from 'react-router';
import { organizationApi } from 'areas/settings/organization/api/organizationApi';
import CategoryList from './CategoryList';
import LoadingSpinner from '@/common/components/LoadingSpinner';

const priorityOptions = [
    { key: '1', text: 'Primary', value: 1 },
    { key: '2', text: 'Custom', value: 2 },
    { key: '3', text: 'nuLogic', value: 3 },
];

const CategoryManagement: React.FC = () => {
    const [priorityFilter, setPriorityFilter] = useState<1 | 2 | 3>(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState<'active' | 'archived'>('active');
    const navigate = useNavigate();

    const {
        data: categories = [],
        isLoading,
        error,
        refetch,
    } = categoryApi.useApiAdminV1CategoryListQuery({ status: statusFilter === 'active' ? 1 : 2 });

    const [updateCategory] = categoryApi.useApiAdminV1CategoryPartialUpdateMutation();
    const { data: organizations = [] } = organizationApi.useApiAdminV1OrganizationListQuery();



    const organizationOptions = [
        { key: 'all', text: 'All Organizations', value: 'all' },
        ...organizations
            .filter(org => !org.is_archived)
            .map(org => ({ key: org.id, text: org.name, value: org.id })),
    ];
    const [organizationFilter, setOrganizationFilter] = useState<'all' | number>('all');


    useEffect(() => {
        const savedFilters = sessionStorage.getItem('categoryFilters');
        if (savedFilters) {
            const { priority, status, organization, page } = JSON.parse(savedFilters);
            setPriorityFilter(priority);
            setStatusFilter(status);
            setOrganizationFilter(organization);
            setCurrentPage(page);
        }
    }, []);

    useEffect(() => {
        sessionStorage.setItem(
            'categoryFilters',
            JSON.stringify({
                priority: priorityFilter,
                status: statusFilter,
                organization: organizationFilter,
                page: currentPage,
            })
        );
    }, [priorityFilter, statusFilter, organizationFilter, currentPage]);


    const handleArchiveToggle = async (category: SurveystructureComplianceRead) => {
        const isArchived = category.status === 2;
        try {
            await updateCategory({
                id: category.id,
                patchedSurveystructureCompliance: {
                    status: isArchived ? 1 : 2,
                },
            }).unwrap();
            refetch();
        } catch (err) {
            console.error(`Failed to ${isArchived ? 'unarchive' : 'archive'} category:`, err);
        }
    };

    const filteredCategories = useMemo(() => categories.filter(cat => {
        const matchesPriority = cat.priority === priorityFilter;
        const matchesStatus =
            (statusFilter === 'active' && cat.status === 1) ||
            (statusFilter === 'archived' && cat.status === 2);
        const matchesOrganization =
            organizationFilter === 'all' || cat.organization === organizationFilter;
        return matchesPriority && matchesStatus && matchesOrganization;
    }), [categories, priorityFilter, statusFilter, organizationFilter]);

    return (
        <PageContent>
            <PageSection>
                <PageHeader
                    title="Category Management"
                    divider
                    controls={
                        <StyledAddButton
                            label="New Category"
                            width="150px"
                            height="34px"
                            onClick={() => {
                                navigate('create');
                            }}
                        />
                    }
                />

                <PageSection display="flex" justifyContent="space-between" alignItems="center">
                    <PageSection>
                        <Dropdown
                            placeholder="Filter by Type"
                            selection
                            value={priorityFilter}
                            options={priorityOptions}
                            onChange={(_, data) => {
                                if (data.value === 3)
                                    setOrganizationFilter('all');
                                setPriorityFilter(data.value as typeof priorityFilter);
                                setCurrentPage(1);
                            }}
                            style={{
                                height: '36px',
                                border: '1px solid #ccc',
                                borderRadius: '5px',
                                fontSize: '14px',
                                minWidth: '200px',
                            }}
                        />

                        <Dropdown
                            placeholder="Filter by Organization"
                            selection
                            value={organizationFilter}
                            options={organizationOptions}
                            disabled={priorityFilter === 3}
                            onChange={(_, data) => {
                                setOrganizationFilter(data.value as typeof organizationFilter);
                                setCurrentPage(1);
                            }}
                            style={{
                                height: '36px',
                                border: '1px solid #ccc',
                                borderRadius: '5px',
                                fontSize: '14px',
                                minWidth: '200px',
                                marginLeft: '12px',
                            }}
                        />
                    </PageSection>

                    <Dropdown
                        placeholder="Filter by Status"
                        selection
                        value={statusFilter}
                        options={[
                            { key: 'active', text: 'Active', value: 'active' },
                            { key: 'archived', text: 'Archived', value: 'archived' },
                        ]}
                        onChange={(_, data) => {
                            setStatusFilter(data.value as typeof statusFilter);
                            setCurrentPage(1);
                        }}
                        style={{
                            height: '36px',
                            border: '1px solid #ccc',
                            borderRadius: '5px',
                            fontSize: '14px',
                            minWidth: '200px',
                            marginLeft: '12px',
                        }}
                    />


                </PageSection>

                {error && <Message negative content="Failed to load categories." />}
                {isLoading ? (
                    <LoadingSpinner />
                ) : (
                    <CategoryList
                        categories={filteredCategories}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                        onNavigate={navigate}
                        onArchiveToggle={handleArchiveToggle}
                        organizations={organizations}
                    />
                )}

            </PageSection>
        </PageContent>
    );
};

export default CategoryManagement;
